/**
 * StreamingCanvas — The main canvas view with tabs, template selector, and streaming content.
 *
 * Uses the production ATS template system (resume-preview--{skin} CSS + SectionContentWithFormatting)
 * instead of a custom renderer. Templates come from TEMPLATE_LIBRARY (28 ATS-certified skins).
 * PDF/DOCX export via resumeDocumentGenerator (text-based, searchable, proper margins).
 */

import {
  ArrowLeft,
  ArrowRight,
  BarChart2,
  Briefcase,
  Check,
  ChevronDown,
  ClipboardList,
  Download,
  ExternalLink,
  FileText,
  GitCompare,
  Mail,
  Save,
  Search,
  Send,
  Sparkles,
  Video,
  X,
  Zap,
} from 'lucide-react';
import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import LoadingSpinner from '../../../components/LoadingSpinner';
import {
  DOCUMENT_LOCALES,
  normalizeDocumentLocale,
  type DocumentLocale,
} from '../utils/documentLanguage';
import { JobMatchRing } from '../../dashboard/components/JobCard';
import JobWorkflowStatusBadge from '../components/JobWorkflowStatusBadge';
import type { AppliedJobInfo } from '../hooks/useAppliedJobsMap';
import { prepareSectionsForExport, sanitizeSectionContentForCanvas } from './canvasSectionUtils';
import { ResumeCanvas } from '../../resume/components/ResumeCanvas';
import VideoSuggestions from '../components/VideoSuggestions';
import { type CanvasTab, type AgentPhase } from './CanvasProvider';
import { useCanvas } from './useCanvas';
import { useCanvasTypewriter } from './useCanvasTypewriter';
import { useCanvasIdleIntervention } from './useCanvasIdleIntervention';
import { SectionContentWithFormatting } from '../../dashboard/components/AIResumeOptimizer/utils/sectionContentRenderer';
import { TEMPLATE_LIBRARY } from '../../dashboard/components/AIResumeOptimizer/ResultsStep';
import { generateResumePDF, generateResumeDOCX, type TemplateId } from '../../../utils/resumeDocumentGenerator';
import { saveAgentArtifacts } from '../../../services/applicationTrackingService';
import { useUserProfile } from '../../dashboard/hooks/useUserProfile';

// ── Helpers ────────────────────────────────────────────────────────────────────

function getTemplateSkin(templateId: string): string {
  return TEMPLATE_LIBRARY.find((t) => t.id === templateId)?.skin ?? 'blue-classic';
}

const FIRST_SECTION_PERSONAL_TITLES = new Set([
  'contact', 'contact information', 'personal information', 'personal details', 'header',
]);

function isFirstSectionPersonalInfo(title: string): boolean {
  return FIRST_SECTION_PERSONAL_TITLES.has(title.toLowerCase().trim());
}

const SECTION_NAME_MAP: Record<string, string> = {
  cover_letter: 'Cover Letter',
  application_note: 'Application Note',
  resume: 'Resume',
};

function humanizeSectionName(name: string): string {
  if (!name) return name;
  if (SECTION_NAME_MAP[name]) return SECTION_NAME_MAP[name];
  return name
    .split(/[_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface StreamingCanvasProps {
  onApply?: () => void;
  onDownload?: (format: 'pdf' | 'docx') => void;
  onEmail?: () => void;
  onBack?: () => void;
  onSaveToProfile?: () => void;
  className?: string;
  jobAppliedInfo?: AppliedJobInfo | null;
  /** Empty canvas — start agentic optimization for the active job */
  onStartOptimization?: () => void;
  /** Empty canvas — switch to Agent chat (mobile split tab) */
  onOpenConversation?: () => void;
  /** Engagement: find similar jobs to the current optimization target */
  onFindSimilarJobs?: () => void;
  /** Engagement: navigate to interview prep */
  onInterviewPrep?: () => void;
  /** Engagement: navigate to track applications */
  onTrackApplications?: () => void;
}

// ── Tab Bar ───────────────────────────────────────────────────────────────────

const TABS: { id: CanvasTab; labelKey: string; fallback: string; icon: typeof FileText; color: string }[] = [
  { id: 'resume', labelKey: 'agent.canvas.tabs.resume', fallback: 'Resume', icon: FileText, color: '#6366f1' },
  { id: 'cover_letter', labelKey: 'agent.canvas.tabs.coverLetter', fallback: 'Cover Letter', icon: Mail, color: '#8b5cf6' },
  { id: 'application_note', labelKey: 'agent.canvas.tabs.applicationNote', fallback: 'Application Note', icon: Send, color: '#10b981' },
];

function TabBar({
  activeTab,
  onTabChange,
  generatingTab,
}: {
  activeTab: CanvasTab;
  onTabChange: (tab: CanvasTab) => void;
  generatingTab?: CanvasTab | null;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="flex items-center gap-1 px-3 border-b overflow-x-auto scrollbar-thin"
      style={{ borderColor: 'var(--ds-border)', minHeight: 44, WebkitOverflowScrolling: 'touch' }}
      role="tablist"
    >
      {TABS.map((tab) => {
        const isActive = activeTab === tab.id;
        const isGenerating = generatingTab === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onTabChange(tab.id)}
            className={`flex items-center gap-1.5 flex-shrink-0 min-h-[44px] px-3 py-2 t-meta font-medium rounded-md transition-all whitespace-nowrap touch-manipulation ds-canvas-tab-btn${isActive ? ' is-active' : ''}${isGenerating ? ' is-generating' : ''}`}
            style={isActive ? undefined : { color: 'var(--ds-text-muted)' }}
            onMouseEnter={(e) => {
              if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = 'var(--ds-bg-tinted, rgba(0,0,0,0.04))';
            }}
            onMouseLeave={(e) => {
              if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = '';
            }}
          >
            <tab.icon className="w-3.5 h-3.5" style={{ color: isActive ? tab.color : undefined }} />
            {t(tab.labelKey, { defaultValue: tab.fallback })}
            {isGenerating && (
              <span className="ds-canvas-tab-btn__pulse" aria-hidden="true" />
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── Template Selector (TEMPLATE_LIBRARY-backed) ────────────────────────────────

// Featured templates — curated high-quality ATS-certified templates shown in the canvas dropdown.
const FEATURED_TEMPLATE_IDS = [
  'executive',
  'ats-optimized',
  'modern-blue',
  'bold-dark',
  'clean-minimal',
  'classic-serif',
  'slate-pro',
  'soft-white',
  'navy-tech',
  'finance-pro',
];

function TemplateSelector({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const featured = TEMPLATE_LIBRARY.filter((t) => FEATURED_TEMPLATE_IDS.includes(t.id));
  const current = TEMPLATE_LIBRARY.find((t) => t.id === value);
  const label = current?.label ?? 'Template';

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className={`ds-btn ds-btn-sm ${
          disabled
            ? 'ds-btn-ghost opacity-50 cursor-not-allowed'
            : 'ds-btn-secondary'
        }`}
        style={!disabled ? { color: 'var(--ds-brand)', borderColor: 'var(--ds-brand)' } : {}}
      >
        <Sparkles className="w-3 h-3" />
        {label}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && !disabled && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="ds-card-pop absolute right-0 mt-1 w-72 max-w-[calc(100vw-2rem)] z-50 py-1 max-h-96 overflow-y-auto">
            <div className="px-3 py-2" style={{ borderBottom: '1px solid var(--ds-border)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--ds-text-muted)' }}>
                ATS-Certified Templates
              </div>
              <div style={{ fontSize: 10, color: 'var(--ds-text-muted)', marginTop: 2 }}>
                All templates pass ATS scanners
              </div>
            </div>
            {featured.map((template) => {
              const isSelected = template.id === value;
              return (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => {
                    onChange(template.id);
                    setOpen(false);
                  }}
                  className="w-full text-left px-3 py-2.5 transition-colors hover:bg-[var(--ds-bg-tinted)]"
                  style={isSelected ? { background: 'var(--ds-brand-soft)', borderLeft: '3px solid var(--ds-brand)' } : { borderLeft: '3px solid transparent' }}
                >
                  <div className="flex items-center gap-2">
                    {/* Color swatch */}
                    <div
                      style={{
                        width: 28, height: 28, borderRadius: 4, flexShrink: 0,
                        background: template.hBg, border: `1.5px solid ${template.ac}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 9, fontWeight: 700, color: template.hTx,
                      }}
                    >
                      Aa
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="flex items-center justify-between">
                        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ds-text)' }}>{template.label}</span>
                        {isSelected && <Check className="w-3.5 h-3.5" style={{ color: 'var(--ds-brand)' }} />}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--ds-text-muted)', marginTop: 1 }}>
                        {template.category} · {template.roles[0]}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ── Version Switcher (Multi-Job) ──────────────────────────────────────────────

function VersionSwitcher({
  versions,
  activeIndex,
  onChange,
}: {
  versions: Array<{ job: { title: string; company: string } }>;
  activeIndex: number;
  onChange: (index: number) => void;
}) {
  const [open, setOpen] = useState(false);

  if (versions.length <= 1) return null;

  const current = versions[activeIndex];

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="ds-btn ds-btn-secondary ds-btn-sm"
      >
        {current.job.title} @ {current.job.company}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="ds-card-pop absolute right-0 mt-1 w-64 z-50 py-1">
            {versions.map((v, i) => (
              <button
                key={i}
                type="button"
                onClick={() => {
                  onChange(i);
                  setOpen(false);
                }}
                className="w-full text-left px-3 py-2 transition-colors hover:bg-[var(--ds-bg-tinted)]"
                style={i === activeIndex ? { background: 'var(--ds-brand-soft)' } : {}}
              >
                <div className="flex items-center justify-between">
                  <span className="t-meta font-medium">
                    {v.job.title} @ {v.job.company}
                  </span>
                  {i === activeIndex && <Check className="w-3.5 h-3.5" style={{ color: 'var(--ds-brand)' }} />}
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Floating Action Bar ───────────────────────────────────────────────────────

function FloatingActionBar({
  phase,
  activeTab,
  isSaving,
  isSaved,
  isApplied,
  onDownload,
  onEmail,
  onApply,
  onSaveToProfile,
  isSavedToProfile,
}: {
  phase: AgentPhase;
  activeTab: CanvasTab;
  isSaving: boolean;
  isSaved: boolean;
  isApplied: boolean;
  onDownload?: (format: 'pdf' | 'docx') => void;
  onEmail?: () => void;
  onApply?: () => void;
  onSaveToProfile?: () => void;
  isSavedToProfile?: boolean;
}) {
  const isEnabled = phase === 'fulfillment' || phase === 'idle';
  const isOptimizing = phase === 'optimization';
  const isCoverLetter = activeTab === 'cover_letter';
  const isApplicationNote = activeTab === 'application_note';

  // Cover letter tab: show download text + email actions
  if (isCoverLetter || isApplicationNote) {
    const label = isCoverLetter ? 'Cover Letter' : 'Application Note';
    return (
      <div
        className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 rounded-full ds-shadow-card border"
        style={{ background: 'var(--ds-bg-card)', backdropFilter: 'blur(8px)', borderColor: 'var(--ds-border)' }}
      >
        <button
          type="button"
          onClick={() => onDownload?.('pdf')}
          disabled={!isEnabled}
          className={`ds-btn ds-btn-sm ${isEnabled ? 'ds-btn-primary' : 'ds-btn-ghost opacity-50 cursor-not-allowed'}`}
        >
          <Download className="w-3 h-3" />
          Download {label}
        </button>
        <button
          type="button"
          onClick={onEmail}
          disabled={!isEnabled}
          className={`ds-btn ds-btn-sm ${isEnabled ? 'ds-btn-secondary' : 'ds-btn-ghost opacity-50 cursor-not-allowed'}`}
        >
          <Mail className="w-3 h-3" />
          Email Me
        </button>
      </div>
    );
  }

  return (
    <div
      className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 rounded-full ds-shadow-card border"
      style={{ background: 'var(--ds-bg-card)', backdropFilter: 'blur(8px)', borderColor: 'var(--ds-border)' }}
    >
      {!isOptimizing && (
        <>
          <button
            type="button"
            onClick={() => onDownload?.('pdf')}
            disabled={!isEnabled}
            className={`ds-btn ds-btn-sm ${isEnabled ? 'ds-btn-primary' : 'ds-btn-ghost opacity-50 cursor-not-allowed'}`}
          >
            <Download className="w-3 h-3" />
            Download PDF
          </button>

          <button
            type="button"
            onClick={onEmail}
            disabled={!isEnabled}
            className={`ds-btn ds-btn-sm ${isEnabled ? 'ds-btn-secondary' : 'ds-btn-ghost opacity-50 cursor-not-allowed'}`}
          >
            <Mail className="w-3 h-3" />
            Email Me
          </button>

          <button
            type="button"
            onClick={onApply}
            disabled={!isEnabled || isApplied}
            className="ds-btn ds-btn-sm ds-btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            style={isApplied ? { color: 'var(--ds-ok)', borderColor: 'var(--ds-ok)' } : {}}
          >
            {isApplied ? <Check className="w-3 h-3" /> : <Send className="w-3 h-3" />}
            {isApplied ? 'Applied' : 'Apply Now'}
          </button>

          {onSaveToProfile && (
            <button
              type="button"
              onClick={onSaveToProfile}
              disabled={!isEnabled || isSavedToProfile}
              className="ds-btn ds-btn-sm ds-btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              style={isSavedToProfile ? { color: 'var(--ds-ok)', borderColor: 'var(--ds-ok)' } : {}}
            >
              {isSavedToProfile ? <Check className="w-3 h-3" /> : <Save className="w-3 h-3" />}
              {isSavedToProfile ? 'Saved to Profile' : 'Save to Profile'}
            </button>
          )}

          {isSaving && (
            <span className="flex items-center gap-1 t-tag t-muted">
              <LoadingSpinner size="xs" showText={false} />
              Saving...
            </span>
          )}
          {isSaved && !isSaving && (
            <span className="flex items-center gap-1 t-tag" style={{ color: 'var(--ds-ok)' }}>
              <Check className="w-3 h-3" />
              Saved
            </span>
          )}
        </>
      )}
    </div>
  );
}

// ── Skill Gap Panel (Phase A) ─────────────────────────────────────────────────

interface SkillGapPanelProps {
  analysis: import('./CanvasProvider').AnalysisResult;
  originalMatchScore: number | null;
  jobTitle: string;
  company: string;
}

function SkillGapPanel({ analysis, originalMatchScore, jobTitle, company }: SkillGapPanelProps) {
  const matchScore = analysis.matchScore;
  const keywordHits = analysis.keywordHits ?? [];
  const keywordMisses = analysis.keywordMisses ?? [];
  const total = keywordHits.length + keywordMisses.length;
  const coveragePct = total > 0 ? Math.round((keywordHits.length / total) * 100) : matchScore;
  const hasImprovement = originalMatchScore != null && originalMatchScore < matchScore;
  const delta = hasImprovement ? matchScore - (originalMatchScore ?? 0) : 0;

  /* Score colour — only applied to arc + number, not backgrounds */
  const scoreColor = matchScore >= 70 ? '#16a34a' : matchScore >= 45 ? '#d97706' : '#dc2626';
  const scoreLabel = matchScore >= 70 ? 'Strong Match' : matchScore >= 45 ? 'Moderate Match' : 'Needs Work';
  const scoreDot   = matchScore >= 70 ? '#22c55e' : matchScore >= 45 ? '#fbbf24' : '#f87171';

  /* Arc gauge math — 260° sweep */
  const R = 38, CX = 48, CY = 48;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const SWEEP = 0.722;
  const startAngle = 130;
  const arcPath = (pct: number) => {
    const span = SWEEP * 360 * (pct / 100);
    const end = startAngle + span;
    const x1 = CX + R * Math.cos(toRad(startAngle));
    const y1 = CY + R * Math.sin(toRad(startAngle));
    const x2 = CX + R * Math.cos(toRad(end));
    const y2 = CY + R * Math.sin(toRad(end));
    const large = span > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2}`;
  };

  return (
    <div style={{ padding: '22px 24px 24px', fontFamily: 'var(--ds-font-sans)' }}>

      {/* ── Score row ── */}
      <div style={{ display: 'flex', gap: 20, alignItems: 'center', marginBottom: 20 }}>

        {/* Arc gauge — compact */}
        <div style={{ flexShrink: 0, position: 'relative', width: 96, height: 96 }}>
          <svg width={96} height={96} viewBox="0 0 96 96">
            <path d={arcPath(100)} fill="none" stroke="#e5e7eb" strokeWidth={8} strokeLinecap="round"/>
            <path d={arcPath(Math.max(matchScore, 1))} fill="none" stroke={scoreColor} strokeWidth={8} strokeLinecap="round"/>
          </svg>
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            paddingTop: 6,
          }}>
            <span style={{ fontSize: 20, fontWeight: 800, lineHeight: 1, color: scoreColor }}>{matchScore}%</span>
            <span style={{ fontSize: 9, fontWeight: 600, color: '#9ca3af', marginTop: 2, letterSpacing: '0.06em', textTransform: 'uppercase' }}>ATS</span>
          </div>
        </div>

        {/* Right meta */}
        <div style={{ flex: 1 }}>
          {/* Label row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              fontSize: 12, fontWeight: 600, color: '#374151',
              background: '#f3f4f6', borderRadius: 6, padding: '3px 10px',
              border: '1px solid #e5e7eb',
            }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: scoreDot, flexShrink: 0, display: 'inline-block' }} />
              {scoreLabel}
            </span>
            {hasImprovement && (
              <span style={{
                fontSize: 11, fontWeight: 600, color: '#6b7280',
                background: '#f9fafb', border: '1px solid #e5e7eb',
                borderRadius: 6, padding: '3px 9px',
              }}>↑ +{delta} pts</span>
            )}
          </div>

          {hasImprovement && (
            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 8 }}>
              <span style={{ textDecoration: 'line-through' }}>{originalMatchScore}%</span>
              <span style={{ margin: '0 5px' }}>→</span>
              <span style={{ fontWeight: 700, color: scoreColor }}>{matchScore}%</span>
            </div>
          )}

          {/* Coverage bar */}
          <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>
            Keyword Coverage
          </div>
          <div style={{ height: 5, borderRadius: 99, background: '#e5e7eb', overflow: 'hidden', marginBottom: 4 }}>
            <div style={{
              height: '100%', borderRadius: 99,
              width: `${coveragePct}%`,
              background: scoreColor,
              transition: 'width 0.8s cubic-bezier(.4,0,.2,1)',
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#9ca3af' }}>
            <span>{keywordHits.length} matched</span>
            <span style={{ color: scoreColor, fontWeight: 600 }}>{coveragePct}%</span>
            <span>{keywordMisses.length} gaps</span>
          </div>
        </div>
      </div>

      {/* ── Divider ── */}
      <div style={{ height: 1, background: '#f0f0f0', marginBottom: 18 }} />

      {/* ── Keyword sections — stacked, neutral ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* Matched */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 9 }}>
            <span style={{
              width: 18, height: 18, borderRadius: '50%',
              background: '#f0fdf4',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M2 5l2.5 2.5L8 3" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Matched
            </span>
            <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400 }}>· {keywordHits.length} keywords</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {keywordHits.length > 0 ? keywordHits.slice(0, 14).map((skill) => (
              <span key={skill} style={{
                fontSize: 11, fontWeight: 500,
                color: '#374151',
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: 6, padding: '3px 10px',
              }}>{skill}</span>
            )) : (
              <span style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic' }}>Populates as resume generates…</span>
            )}
          </div>
        </div>

        {/* Gaps / Missing */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 9 }}>
            <span style={{
              width: 18, height: 18, borderRadius: '50%',
              background: keywordMisses.length > 0 ? '#fffbeb' : '#f0fdf4',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              {keywordMisses.length > 0 ? (
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M5 2.5v3M5 7v.5" stroke="#d97706" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              ) : (
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M2 5l2.5 2.5L8 3" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </span>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {keywordMisses.length > 0 ? 'Gaps to Fill' : 'All Covered'}
            </span>
            {keywordMisses.length > 0 && (
              <span style={{ fontSize: 11, color: '#9ca3af' }}>· {keywordMisses.length} keywords</span>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {keywordMisses.length > 0 ? keywordMisses.slice(0, 14).map((skill) => (
              <span key={skill} style={{
                fontSize: 11, fontWeight: 500,
                color: '#92400e',
                background: '#fefce8',
                border: '1px solid #fde68a',
                borderRadius: 6, padding: '3px 10px',
              }}>{skill}</span>
            )) : (
              <span style={{ fontSize: 11, color: '#6b7280' }}>
                {keywordHits.length > 0 ? 'All required keywords are covered in your resume.' : 'Analyzing…'}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Section Skeleton Loader ───────────────────────────────────────────────────

function SectionSkeleton({ title }: { title: string }) {
  return (
    <div
      className="resume-preview-section-wrapper mx-0"
      style={{ opacity: 0.6 }}
    >
      <div className="resume-preview-section-card overflow-hidden animate-pulse">
        <div className="resume-preview-section-header">
          <h4 style={{ margin: 0 }}>{title.toUpperCase()}</h4>
        </div>
        <div className="resume-preview-section-body space-y-2 p-3">
          <div className="h-3 rounded" style={{ background: 'var(--ds-border)', width: '85%' }} />
          <div className="h-3 rounded" style={{ background: 'var(--ds-border)', width: '70%' }} />
          <div className="h-3 rounded" style={{ background: 'var(--ds-border)', width: '90%' }} />
          <div className="h-3 rounded" style={{ background: 'var(--ds-border)', width: '60%' }} />
        </div>
      </div>
    </div>
  );
}

// ── Diff Section View ────────────────────────────────────────────────────────

function DiffSectionView({
  title,
  original,
  optimized,
}: {
  title: string;
  original: string | undefined;
  optimized: string;
}) {
  const origLines = (original ?? '').split('\n').filter(Boolean);
  const optLines = optimized.split('\n').filter(Boolean);

  return (
    <div className="rounded-lg overflow-hidden border mb-3" style={{ borderColor: 'var(--ds-border)' }}>
      <div
        className="px-3 py-1.5 t-label font-semibold"
        style={{ background: 'var(--ds-bg-tinted)', borderBottom: '1px solid var(--ds-border)' }}
      >
        {title}
      </div>
      <div className="p-3 space-y-0.5 t-tag font-mono text-[11px]">
        {origLines.map((line, i) =>
          !optLines.includes(line) ? (
            <div key={`r-${i}`} className="px-1.5 rounded" style={{ background: '#fee2e2', color: '#991b1b', textDecoration: 'line-through' }}>
              − {line}
            </div>
          ) : null
        )}
        {optLines.map((line, i) =>
          !origLines.includes(line) ? (
            <div key={`a-${i}`} className="px-1.5 rounded" style={{ background: '#dcfce7', color: '#166534' }}>
              + {line}
            </div>
          ) : (
            <div key={`u-${i}`} className="px-1.5" style={{ color: DOC_TEXT_MUTED }}>
              &nbsp; {line}
            </div>
          )
        )}
      </div>
    </div>
  );
}

// ── Placeholder Badge Renderer ───────────────────────────────────────────────
// Converts [Bracket Placeholder] tokens in content into highlighted clickable badges.

function hasPlaceholders(content: string): boolean {
  return /\[[^\]]{3,80}\]/.test(content);
}

/** Render a text segment with **bold** markdown processed and placeholder tokens as clickable chips. */
function renderDocSegment(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} style={{ fontWeight: 700 }}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

function PlaceholderContent({
  content,
  onPlaceholderClick,
}: {
  content: string;
  onPlaceholderClick?: (placeholder: string) => void;
}) {
  const lines = content.split('\n');

  return (
    <div style={{ fontFamily: DOC_FONT, fontSize: DOC_FONT_SIZE, lineHeight: DOC_LINE_HEIGHT, color: DOC_TEXT }}>
      {lines.map((line, li) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={li} style={{ height: '6px' }} />;

        const isBullet = /^[-•●▪◦]\s/.test(trimmed);
        const lineText = isBullet ? trimmed.replace(/^[-•●▪◦]\s/, '') : trimmed;

        // Split by placeholder tokens [...]
        const re = /\[([^\]]{3,80})\]/g;
        const parts: React.ReactNode[] = [];
        let last = 0;
        let match: RegExpExecArray | null;
        while ((match = re.exec(lineText)) !== null) {
          if (match.index > last) parts.push(renderDocSegment(lineText.slice(last, match.index)));
          const token = match[1];
          parts.push(
            <button
              key={`ph-${li}-${match.index}`}
              type="button"
              onClick={() => onPlaceholderClick?.(token)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                padding: '0px 6px', borderRadius: 4,
                fontSize: '10px', fontWeight: 600,
                background: '#fef3c7', color: '#92400e',
                border: '1px dashed #d97706',
                cursor: 'pointer', verticalAlign: 'middle',
                fontFamily: DOC_FONT,
              }}
              title={`Click to fill in: ${token}`}
            >
              + {token}
            </button>
          );
          last = match.index + match[0].length;
        }
        if (last < lineText.length) parts.push(renderDocSegment(lineText.slice(last)));

        const inner = <>{parts}</>;
        if (isBullet) {
          return (
            <div key={li} style={{ display: 'flex', gap: '6px', marginBottom: '1px' }}>
              <span style={{ flexShrink: 0 }}>•</span>
              <span>{inner}</span>
            </div>
          );
        }
        // Bold/role lines (contains | or starts with bold)
        const isBoldLine = /\*\*/.test(line) || /\|\s/.test(line);
        return (
          <div key={li} style={{ fontWeight: isBoldLine ? 600 : 400, marginTop: isBoldLine ? '7px' : 0, marginBottom: '1px' }}>
            {inner}
          </div>
        );
      })}
    </div>
  );
}

// ── Inline Section Editor ────────────────────────────────────────────────────

interface InlineSectionEditorProps {
  content: string;
  onSave: (newContent: string) => void;
  onCancel: () => void;
}

function InlineSectionEditor({ content, onSave, onCancel }: InlineSectionEditorProps) {
  const [draft, setDraft] = React.useState(content);
  const ref = React.useRef<HTMLTextAreaElement>(null);

  React.useEffect(() => {
    ref.current?.focus();
    ref.current?.setSelectionRange(0, 0);
  }, []);

  return (
    <div className="relative">
      <textarea
        ref={ref}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        className="w-full t-meta leading-relaxed resize-none rounded-lg border-2 p-3 outline-none focus:outline-none"
        style={{
          minHeight: '120px',
          borderColor: 'var(--ds-brand)',
          background: '#ffffff',
          color: DOC_TEXT,
          fontFamily: 'inherit',
        }}
        rows={Math.max(6, draft.split('\n').length + 2)}
      />
      <div className="flex items-center gap-2 mt-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="ds-btn ds-btn-ghost ds-btn-sm"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onSave(draft)}
          className="ds-btn ds-btn-primary ds-btn-sm"
        >
          <Check className="w-3 h-3" />
          Save
        </button>
      </div>
    </div>
  );
}

// ── Rail Button ───────────────────────────────────────────────────────────────
// Reusable icon+label button for the right-side sticky inline rail.

function RailButton({
  icon, label, title, onClick, disabled, accent, color, hoverBg,
}: {
  icon: React.ReactNode;
  label: string;
  title: string;
  onClick: () => void;
  disabled?: boolean;
  accent?: boolean;
  color?: string;
  hoverBg?: string;
}) {
  const baseColor = accent ? '#fff' : (color ?? 'var(--ds-text-muted)');
  const baseBg = accent ? 'var(--ds-brand)' : 'none';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
        background: baseBg, color: baseColor, border: 'none', borderRadius: 7,
        padding: accent ? '6px 7px' : '5px 7px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        fontSize: 9, fontWeight: accent ? 700 : 500,
        letterSpacing: '0.03em', minWidth: 36,
        transition: 'background 0.15s, opacity 0.15s',
      }}
      onMouseEnter={(e) => {
        if (!disabled && !accent) (e.currentTarget as HTMLButtonElement).style.background = hoverBg ?? 'rgba(0,0,0,0.05)';
      }}
      onMouseLeave={(e) => {
        if (!accent) (e.currentTarget as HTMLButtonElement).style.background = 'none';
      }}
    >
      {icon}
      {label}
    </button>
  );
}

// ── Letter Placeholder Body Renderer ─────────────────────────────────────────
// Parses [Bracket Placeholder] tokens in letter body text and renders them as
// amber-tinted badges so the user knows to fill them in before exporting.

function LetterPlaceholderBody({ content, isStreaming }: { content: string; isStreaming: boolean }) {
  const { displayedText, isTyping } = useCanvasTypewriter(content, { isStreaming, speed: 15 });
  // After generation finishes, always show the stored letter — never leave a
  // typewriter fragment on screen while the agent says "ready".
  const textToRender = isStreaming && isTyping ? displayedText : content;
  const lines = textToRender.split('\n');
  const re = /\[([^\]]{3,80})\]/g;

  return (
    <div className="whitespace-pre-wrap">
      {lines.map((line, li) => {
        const parts: React.ReactNode[] = [];
        let last = 0;
        let match: RegExpExecArray | null;
        re.lastIndex = 0;
        while ((match = re.exec(line)) !== null) {
          if (match.index > last) parts.push(line.slice(last, match.index));
          const token = match[1];
          parts.push(
            <mark
              key={`ph-${li}-${match.index}`}
              className="ds-canvas-letter__placeholder"
              title={`Fill in: ${token}`}
            >
              [{token}]
            </mark>
          );
          last = match.index + match[0].length;
        }
        if (last < line.length) parts.push(line.slice(last));
        return (
          <React.Fragment key={`line-${li}`}>
            {parts}
            {li < lines.length - 1 && '\n'}
          </React.Fragment>
        );
      })}
      {(isTyping || isStreaming) && (
        <span
          className="inline-block w-2 h-4 ml-1 bg-purple-500 animate-pulse align-middle rounded-[1px]"
          aria-hidden="true"
        />
      )}
    </div>
  );
}

// ── Letter Inline Editor ──────────────────────────────────────────────────────
// Shown when the user clicks to edit a letter body. Lightweight textarea with
// a floating glassmorphism BubbleToolbar on selection.

function LetterInlineEditor({
  draft,
  onChange,
  onSave,
  onCancel,
}: {
  draft: string;
  onChange: (v: string) => void;
  onSave: (v: string) => void;
  onCancel: () => void;
}) {
  const ref = React.useRef<HTMLTextAreaElement>(null);

  React.useEffect(() => {
    ref.current?.focus();
  }, []);

  return (
    <div className="relative">
      <BubbleToolbar targetRef={ref} />
      <textarea
        ref={ref}
        value={draft}
        onChange={(e) => onChange(e.target.value)}
        className="ds-canvas-letter__editor w-full outline-none resize-none"
        style={{ minHeight: '20rem' }}
        rows={Math.max(10, draft.split('\n').length + 3)}
      />
      <div className="flex items-center gap-2 mt-4 pt-3 border-t ds-canvas-letter__divider">
        <button
          type="button"
          onClick={() => onSave(draft)}
          className="ds-btn ds-btn-primary ds-btn-sm"
        >
          <Check className="w-3 h-3" />
          Save
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="ds-btn ds-btn-ghost ds-btn-sm"
        >
          Discard
        </button>
      </div>
    </div>
  );
}

// ── Inline Selection BubbleMenu Toolbar ───────────────────────────────────────
// Appears above any selected text inside the LetterInlineEditor textarea.
// Glassmorphism pill with Improve / Shorten / Copy actions.

function BubbleToolbar({ targetRef }: { targetRef: React.RefObject<HTMLTextAreaElement> }) {
  const { t } = useTranslation();
  const [toolbar, setToolbar] = React.useState<{ x: number; y: number; text: string } | null>(null);
  const toolbarRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const el = targetRef.current;
    if (!el) return;

    const onSelect = () => {
      const start = el.selectionStart ?? 0;
      const end = el.selectionEnd ?? 0;
      if (start === end) { setToolbar(null); return; }

      const selected = el.value.slice(start, end).trim();
      if (!selected) { setToolbar(null); return; }

      // Approximate position: use a mirrored div technique
      const rect = el.getBoundingClientRect();
      const containerRect = el.parentElement?.getBoundingClientRect() ?? rect;
      // Simple heuristic: center-top of the textarea + a fixed offset
      setToolbar({
        x: rect.left - containerRect.left + rect.width / 2,
        y: rect.top - containerRect.top - 8,
        text: selected,
      });
    };

    el.addEventListener('mouseup', onSelect);
    el.addEventListener('keyup', onSelect);
    return () => {
      el.removeEventListener('mouseup', onSelect);
      el.removeEventListener('keyup', onSelect);
    };
  }, [targetRef]);

  // Hide toolbar on click outside
  React.useEffect(() => {
    const hide = (e: MouseEvent) => {
      if (toolbarRef.current && !toolbarRef.current.contains(e.target as Node)) {
        const el = targetRef.current;
        if (!el || !el.contains(e.target as Node)) setToolbar(null);
      }
    };
    document.addEventListener('mousedown', hide);
    return () => document.removeEventListener('mousedown', hide);
  }, [targetRef]);

  if (!toolbar) return null;

  const handleAction = (action: 'copy' | 'shorten' | 'improve') => {
    if (action === 'copy') {
      void navigator.clipboard.writeText(toolbar.text);
      setToolbar(null);
    }
    // 'shorten' / 'improve' — hook into LLM in a future iteration
    setToolbar(null);
  };

  return (
    <div
      ref={toolbarRef}
      style={{
        position: 'absolute',
        left: toolbar.x,
        top: toolbar.y,
        transform: 'translate(-50%, -100%)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        padding: '4px 6px',
        background: 'rgba(15,23,42,0.80)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderRadius: 10,
        border: '1px solid rgba(255,255,255,0.12)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.30)',
        pointerEvents: 'all',
      }}
    >
      {[
        { id: 'improve', label: '✨ Improve' },
        { id: 'shorten', label: '✂ Shorten' },
      ].map((action) => (
        <button
          key={action.id}
          type="button"
          onMouseDown={(e) => { e.preventDefault(); handleAction(action.id as 'improve' | 'shorten'); }}
          style={{
            padding: '3px 9px',
            fontSize: 11,
            fontWeight: 600,
            color: '#e2e8f0',
            background: 'transparent',
            border: 'none',
            borderRadius: 7,
            cursor: 'pointer',
            transition: 'background 0.12s',
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.12)'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
        >
          {action.label}
        </button>
      ))}
      <div style={{ width: 1, height: 14, background: 'rgba(255,255,255,0.15)', margin: '0 2px' }} />
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); handleAction('copy'); }}
        style={{
          padding: '3px 9px',
          fontSize: 11,
          fontWeight: 500,
          color: '#94a3b8',
          background: 'transparent',
          border: 'none',
          borderRadius: 7,
          cursor: 'pointer',
          transition: 'background 0.12s, color 0.12s',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.12)';
          (e.currentTarget as HTMLButtonElement).style.color = '#fff';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
          (e.currentTarget as HTMLButtonElement).style.color = '#94a3b8';
        }}
      >
        {t('agent.canvas.copy', 'Copy')}
      </button>
      {/* Caret pointer */}
      <div
        style={{
          position: 'absolute',
          bottom: -5,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '5px solid transparent',
          borderRight: '5px solid transparent',
          borderTop: '5px solid rgba(15,23,42,0.80)',
        }}
      />
    </div>
  );
}

// ── Cover Letter View ─────────────────────────────────────────────────────────

interface LetterViewProps {
  content: string | null;
  /** Live-streaming buffer (state.streamingContent) — rendered while isStreaming. */
  streamingContent?: string;
  isStreaming: boolean;
  candidateName?: string;
  candidateEmail?: string;
  candidatePhone?: string;
  jobTitle?: string;
  company?: string;
  /** Called when the user clicks the "Generate" CTA in the empty state. */
  onGenerate?: () => void;
}

function CoverLetterView({
  content,
  streamingContent,
  isStreaming,
  candidateName,
  candidateEmail,
  candidatePhone,
  jobTitle,
  company,
  onGenerate,
}: LetterViewProps) {
  const { t, i18n } = useTranslation();
  const displayContent = isStreaming ? (streamingContent ?? '') : (content ?? '');
  const hasContent = displayContent.length > 0;

  const [isEditing, setIsEditing] = React.useState(false);
  const [editDraft, setEditDraft] = React.useState('');

  const handleBodyClick = () => {
    if (!isStreaming && hasContent && !isEditing) {
      setEditDraft(displayContent);
      setIsEditing(true);
    }
  };

  if (!hasContent && !isStreaming) {
    return (
      <div className="ds-canvas-doc-empty">
        <div className="ds-canvas-doc-empty__orb">
          <Mail style={{ width: 32, height: 32, color: '#fff' }} />
        </div>
        <div>
          <div className="ds-canvas-doc-empty__title">
            {t('agent.canvas.tabs.coverLetter', { defaultValue: 'Cover Letter' })}
          </div>
          <div className="ds-canvas-doc-empty__desc">
            {t('agent.canvas.coverLetter.emptyDesc', {
              defaultValue: 'A tailored cover letter for{{role}}{{company}}. Written to match the job description and your experience.',
              role: jobTitle ? ` ${jobTitle}` : ' this role',
              company: company ? ` at ${company}` : '',
            })}
          </div>
        </div>
        <button
          type="button"
          onClick={onGenerate}
          disabled={!onGenerate}
          className={`ds-btn ds-btn-primary ds-canvas-doc-empty__btn touch-manipulation${onGenerate ? '' : ' opacity-50 cursor-not-allowed'}`}
        >
          <Sparkles style={{ width: 15, height: 15 }} />
          {t('agent.canvas.coverLetter.writeCta', { defaultValue: 'Write Cover Letter' })}
        </button>
        {!onGenerate && (
          <p className="ds-canvas-doc-empty__hint">
            {t('agent.canvas.coverLetter.optimizeFirst', { defaultValue: 'Optimize a resume first to unlock this.' })}
          </p>
        )}
      </div>
    );
  }

  const today = new Date().toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="ds-canvas-letter-shell">
      <div
        className={`ds-canvas-letter relative w-full transition-all duration-300 ${isStreaming ? 'is-streaming ring-2 ring-indigo-500 ring-offset-background rounded-md' : ''}`}
        data-canvas-focus-card={isStreaming ? 'cover_letter' : undefined}
        style={{
          fontFamily: "'Georgia', 'Times New Roman', serif",
        }}
      >
        {/* Sender block */}
        {(candidateName || candidateEmail || candidatePhone) && (
          <div className="mb-6 pb-4 border-b ds-canvas-letter__divider">
            {candidateName && (
              <div className="ds-canvas-letter__heading" style={{ fontSize: '16px' }}>{candidateName}</div>
            )}
            <div className="ds-canvas-letter__meta" style={{ fontSize: '12px', marginTop: '2px' }}>
              {[candidateEmail, candidatePhone].filter(Boolean).join('  ·  ')}
            </div>
          </div>
        )}

        {/* Date */}
        <div className="ds-canvas-letter__date" style={{ marginBottom: '20px' }}>{today}</div>

        {/* Recipient */}
        {(company || jobTitle) && (
          <div className="ds-canvas-letter__recipient" style={{ marginBottom: '24px' }}>
            <div>{t('agent.canvas.letter.hiringManager', { defaultValue: 'Hiring Manager' })}</div>
            {company && <div className="ds-canvas-letter__heading" style={{ fontWeight: 600 }}>{company}</div>}
            {jobTitle && (
              <div style={{ fontStyle: 'italic' }}>
                {t('agent.canvas.letter.rePrefix', { defaultValue: 'Re: {{title}}', title: jobTitle })}
              </div>
            )}
          </div>
        )}

        {/* Salutation */}
        <div className="ds-canvas-letter__salutation" style={{ marginBottom: '16px', fontWeight: 500 }}>
          {t('agent.canvas.letter.salutation', { defaultValue: 'Dear Hiring Manager,' })}
        </div>

        {/* Body */}
        {isEditing ? (
          <LetterInlineEditor
            draft={editDraft}
            onChange={setEditDraft}
            onSave={(saved) => { setIsEditing(false); setEditDraft(saved); }}
            onCancel={() => setIsEditing(false)}
          />
        ) : (
          <div
            onClick={handleBodyClick}
            title={!isStreaming ? t('agent.canvas.letter.clickToEdit', { defaultValue: 'Click anywhere to edit' }) : undefined}
            className="ds-canvas-letter__body"
            style={{
              cursor: !isStreaming ? 'text' : 'default',
              position: 'relative',
            }}
          >
            <LetterPlaceholderBody content={displayContent} isStreaming={isStreaming} />
            {/* Click-to-edit micro-tip */}
            {!isStreaming && hasContent && (
              <div
                className="ds-canvas-letter__hint"
                style={{
                  marginTop: 20,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  userSelect: 'none',
                }}
              >
                <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                  <path d="M7.5 1.5l2 2-6 6H1.5v-2l6-6z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {t('agent.canvas.letter.clickToEdit', { defaultValue: 'Click anywhere to edit' })}
              </div>
            )}
          </div>
        )}

        {/* Closing salutation */}
        {hasContent && !isStreaming && (
          <div className="ds-canvas-letter__closing" style={{ marginTop: '28px' }}>
            <div style={{ fontWeight: 500 }}>
              {t('agent.canvas.letter.closing', { defaultValue: 'Sincerely,' })}
            </div>
            {candidateName && (
              <div style={{ marginTop: '4px' }}>{candidateName}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Application Note View ─────────────────────────────────────────────────────

function ApplicationNoteView({
  content,
  streamingContent,
  isStreaming,
  candidateName,
  jobTitle,
  company,
  onGenerate,
}: LetterViewProps) {
  const { t, i18n } = useTranslation();
  const displayContent = isStreaming ? (streamingContent ?? '') : (content ?? '');
  const hasContent = displayContent.length > 0;

  const [isEditing, setIsEditing] = React.useState(false);
  const [editDraft, setEditDraft] = React.useState('');

  const handleBodyClick = () => {
    if (!isStreaming && hasContent && !isEditing) {
      setEditDraft(displayContent);
      setIsEditing(true);
    }
  };

  if (!hasContent && !isStreaming) {
    return (
      <div className="ds-canvas-doc-empty">
        <div className="ds-canvas-doc-empty__orb">
          <Send style={{ width: 30, height: 30, color: '#fff' }} />
        </div>
        <div>
          <div className="ds-canvas-doc-empty__title">
            {t('agent.canvas.tabs.applicationNote', { defaultValue: 'Application Note' })}
          </div>
          <div className="ds-canvas-doc-empty__desc">
            {t('agent.canvas.applicationNote.emptyDesc', {
              defaultValue: 'A short, punchy note for the "Why hire me?" or "Additional info" field in the application form{{role}}.',
              role: jobTitle ? ` for ${jobTitle}` : '',
            })}
          </div>
        </div>
        <button
          type="button"
          onClick={onGenerate}
          disabled={!onGenerate}
          className={`ds-btn ds-btn-primary ds-canvas-doc-empty__btn touch-manipulation${onGenerate ? '' : ' opacity-50 cursor-not-allowed'}`}
        >
          <Sparkles style={{ width: 15, height: 15 }} />
          {t('agent.canvas.applicationNote.writeCta', { defaultValue: 'Write Application Note' })}
        </button>
        {!onGenerate && (
          <p className="ds-canvas-doc-empty__hint">
            {t('agent.canvas.coverLetter.optimizeFirst', { defaultValue: 'Optimize a resume first to unlock this.' })}
          </p>
        )}
      </div>
    );
  }

  const today = new Date().toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="ds-canvas-letter-shell">
      <div
        className={`ds-canvas-letter relative w-full transition-all duration-300 ${isStreaming ? 'is-streaming ring-2 ring-indigo-500 ring-offset-background rounded-md' : ''}`}
        data-canvas-focus-card={isStreaming ? 'application_note' : undefined}
        style={{
          fontFamily: 'var(--ds-font-sans)',
        }}
      >
        {/* Floating copy button */}
        <button
          type="button"
          onClick={() => { if (displayContent) void navigator.clipboard.writeText(displayContent); }}
          className="ds-btn ds-btn-sm ds-btn-ghost absolute top-3 right-3"
          disabled={!hasContent}
          style={{ opacity: 0.7 }}
        >
          {t('agent.canvas.copy', 'Copy')}
        </button>

        {/* Sender block */}
        {candidateName && (
          <div className="mb-5 pb-3 border-b ds-canvas-letter__divider">
            <div className="ds-canvas-letter__heading" style={{ fontSize: '15px' }}>{candidateName}</div>
          </div>
        )}

        {/* Date */}
        <div className="ds-canvas-letter__date" style={{ fontSize: '12.5px', marginBottom: '18px' }}>{today}</div>

        {/* Recipient */}
        {(company || jobTitle) && (
          <div className="ds-canvas-letter__recipient" style={{ fontSize: '12.5px', marginBottom: '20px' }}>
            <div>Hiring Team</div>
            {company && <div className="ds-canvas-letter__heading" style={{ fontWeight: 600 }}>{company}</div>}
            {jobTitle && <div style={{ fontStyle: 'italic' }}>Re: Application for {jobTitle}</div>}
          </div>
        )}

        {/* Salutation */}
        <div className="ds-canvas-letter__salutation" style={{ marginBottom: '14px', fontWeight: 500, fontSize: '12.5px' }}>
          {t('agent.canvas.letter.salutationTeam', { defaultValue: 'Dear Hiring Team,' })}
        </div>

        {/* Body */}
        {isEditing ? (
          <LetterInlineEditor
            draft={editDraft}
            onChange={setEditDraft}
            onSave={(saved) => { setIsEditing(false); setEditDraft(saved); }}
            onCancel={() => setIsEditing(false)}
          />
        ) : (
          <div
            onClick={handleBodyClick}
            title={!isStreaming ? t('agent.canvas.letter.clickToEdit', { defaultValue: 'Click anywhere to edit' }) : undefined}
            className="ds-canvas-letter__body"
            style={{ cursor: !isStreaming ? 'text' : 'default' }}
          >
            <LetterPlaceholderBody content={displayContent} isStreaming={isStreaming} />
            {!isStreaming && hasContent && (
              <div
                className="ds-canvas-letter__hint"
                style={{
                  marginTop: 20,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  userSelect: 'none',
                }}
              >
                <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                  <path d="M7.5 1.5l2 2-6 6H1.5v-2l6-6z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {t('agent.canvas.letter.clickToEdit', { defaultValue: 'Click anywhere to edit' })}
              </div>
            )}
          </div>
        )}

        {/* Closing salutation */}
        {hasContent && !isStreaming && (
          <div className="ds-canvas-letter__closing" style={{ marginTop: '24px', fontSize: '12.5px' }}>
            <div style={{ fontWeight: 500 }}>
              {t('agent.canvas.letter.closingBest', { defaultValue: 'Best regards,' })}
            </div>
            {candidateName && (
              <div style={{ marginTop: '4px' }}>{candidateName}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Action Dock — centered on the right edge of the canvas, expands as vertical pill ──

interface ActionMenuProps {
  isExporting: 'pdf' | 'docx' | null;
  onDownload: (format: 'pdf' | 'docx') => void;
  onEmail?: () => void;
  onApply?: () => void;
  onSkillGap: () => void;
  hasSkillGap: boolean;
  isApplied: boolean;
  jobTitle?: string;
  /** Horizontal toolbar in canvas header (avoids overlap with agent floating nav). */
  inline?: boolean;
}

function ActionMenu({
  isExporting,
  onDownload,
  onEmail,
  onApply,
  onSkillGap,
  hasSkillGap,
  isApplied,
  jobTitle,
  inline = false,
}: ActionMenuProps) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  /* Shared icon-button style for the pill dock */
  const dockBtn = (color = 'var(--ds-text-body)'): React.CSSProperties => ({
    width: 46, height: 46,
    borderRadius: '50%',
    border: 'none',
    background: 'transparent',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer',
    color,
    flexShrink: 0,
    transition: 'background 0.13s, transform 0.13s',
    position: 'relative',
  });

  if (inline) {
    return (
      <div ref={ref} className="ds-canvas-action-menu flex items-center gap-0.5 shrink-0">
        <button type="button" className="ds-canvas-action-menu__btn ds-btn ds-btn-ghost ds-btn-sm touch-manipulation" title="Skill gap" disabled={!hasSkillGap} onClick={() => hasSkillGap && onSkillGap()} style={{ color: hasSkillGap ? '#f59e0b' : undefined }}>
          <BarChart2 className="w-4 h-4" />
        </button>
        <button type="button" className="ds-canvas-action-menu__btn ds-btn ds-btn-ghost ds-btn-sm touch-manipulation" title="Download PDF" disabled={!!isExporting} onClick={() => onDownload('pdf')} style={{ color: '#ef4444' }}>
          <Download className="w-4 h-4" />
        </button>
        <button type="button" className="ds-canvas-action-menu__btn ds-btn ds-btn-ghost ds-btn-sm touch-manipulation" title="Download DOCX" disabled={!!isExporting} onClick={() => onDownload('docx')} style={{ color: '#3b82f6' }}>
          <FileText className="w-4 h-4" />
        </button>
        {onEmail && (
          <button type="button" className="ds-canvas-action-menu__btn ds-btn ds-btn-ghost ds-btn-sm touch-manipulation" title="Email resume" onClick={onEmail} style={{ color: '#8b5cf6' }}>
            <Mail className="w-4 h-4" />
          </button>
        )}
        {onApply && jobTitle && (
          <button type="button" className="ds-canvas-action-menu__btn ds-btn ds-btn-ghost ds-btn-sm touch-manipulation" title={isApplied ? 'Applied' : 'Apply to job'} onClick={onApply} style={{ color: isApplied ? '#10b981' : '#6366f1' }}>
            <ExternalLink className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {/* Trigger — always brand-colored so it's easy to spot */}
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        title={open ? 'Close toolbar' : 'Open toolbar'}
        style={{
          width: 46, height: 46,
          borderRadius: '50%',
          border: 'none',
          background: open ? '#4f46e5' : 'var(--ds-brand, #6366f1)',
          boxShadow: '0 3px 16px rgba(99,102,241,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
          color: '#fff',
          transition: 'background 0.15s, box-shadow 0.15s, transform 0.15s',
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.08)'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'; }}
      >
        {open ? (
          <X style={{ width: 17, height: 17 }} />
        ) : (
          <svg width="19" height="19" viewBox="0 0 16 16" fill="none">
            <path d="M2 4h12M2 8h12M2 12h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <circle cx="6" cy="4" r="1.8" fill="rgba(255,255,255,0.2)" stroke="currentColor" strokeWidth="1.4"/>
            <circle cx="10" cy="8" r="1.8" fill="rgba(255,255,255,0.2)" stroke="currentColor" strokeWidth="1.4"/>
            <circle cx="5" cy="12" r="1.8" fill="rgba(255,255,255,0.2)" stroke="currentColor" strokeWidth="1.4"/>
          </svg>
        )}
      </button>

      {/* Vertical pill dock — slides open below the trigger */}
      {open && (
        <div
          style={{
            marginTop: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
            background: 'var(--ds-bg-card)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
            border: '1px solid var(--ds-border)',
            borderRadius: 28,
            padding: '10px 5px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.22), 0 2px 8px rgba(0,0,0,0.10)',
          }}
        >
          {/* Skill Gap — always visible, full color */}
          <button
            type="button"
            title={hasSkillGap ? 'Skill Gap Analysis' : 'Skill gap not available yet'}
            style={dockBtn('var(--ds-brand,#6366f1)')}
            onClick={() => { if (hasSkillGap) { onSkillGap(); setOpen(false); } }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(99,102,241,0.09)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          >
            <BarChart2 style={{ width: 19, height: 19 }} />
          </button>

          <div style={{ width: 28, height: 1, background: 'var(--ds-border)', margin: '2px 0' }} />

          {/* PDF */}
          <button
            type="button"
            title="Download PDF"
            style={dockBtn('#dc2626')}
            onClick={() => { onDownload('pdf'); setOpen(false); }}
            disabled={!!isExporting}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(220,38,38,0.07)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          >
            {isExporting === 'pdf'
              ? <span style={{ fontSize: 10, fontWeight: 700, color: '#dc2626' }}>…</span>
              : <Download style={{ width: 19, height: 19 }} />}
          </button>

          {/* DOCX */}
          <button
            type="button"
            title="Download DOCX"
            style={dockBtn('#2563eb')}
            onClick={() => { onDownload('docx'); setOpen(false); }}
            disabled={!!isExporting}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(37,99,235,0.07)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          >
            {isExporting === 'docx'
              ? <span style={{ fontSize: 10, fontWeight: 700, color: '#2563eb' }}>…</span>
              : <FileText style={{ width: 19, height: 19 }} />}
          </button>

          {onEmail && (
            <button
              type="button"
              title="Email to Me"
              style={dockBtn('#0891b2')}
              onClick={() => { onEmail(); setOpen(false); }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(8,145,178,0.07)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
            >
              <Mail style={{ width: 19, height: 19 }} />
            </button>
          )}

          {onApply && jobTitle && (
            <>
              <div style={{ width: 28, height: 1, background: 'var(--ds-border)', margin: '2px 0' }} />
              <button
                type="button"
                title={isApplied ? 'Applied ✓' : 'Apply Now'}
                style={dockBtn('#15803d')}
                onClick={() => { onApply(); setOpen(false); }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(21,128,61,0.07)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
              >
                <ExternalLink style={{ width: 19, height: 19 }} />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Clean ATS Resume Document Renderer ────────────────────────────────────────
// Word-document-style renderer: white page, black text, proper A4 margins.
// No template colors, no brand decorations — output matches PDF/DOCX exactly.

// Use the app's own design-system tokens so the resume matches the agent panel font exactly.
const DOC_FONT      = 'var(--ds-font-sans)';
const DOC_FONT_SIZE = 'var(--ds-fs-body)';
const DOC_LINE_HEIGHT = 'var(--ds-lh-relaxed)';
const DOC_TEXT = 'var(--ds-text-body, #e5e7eb)';
const DOC_TEXT_STRONG = 'var(--ds-text-strong, #f3f4f6)';
const DOC_TEXT_MUTED = 'var(--ds-text-muted, #9ca3af)';
const DOC_BORDER = 'var(--ds-border, rgba(255,255,255,0.12))';

interface ResumePreviewProps {
  sections: Array<{ title: string; content: string }>;
  templateId: string;
  streamingSection: string | null;
  streamingContent: string;
  pendingSections: string[];
  showDiff: boolean;
  originalSections: Array<{ title: string; content: string }> | null;
  onSectionSave?: (title: string, newContent: string) => void;
  isOptimizing?: boolean;
  streamingAnchorRef?: React.RefObject<HTMLDivElement | null>;
  getSectionLabel?: (title: string) => string;
  /** ATS score overlay — shown in the top-right corner of the page, excluded from PDF/DOCX. */
  matchScore?: number | null;
  originalMatchScore?: number | null;
  /** Title of section currently being optimized — highlights + auto-scrolls to it. */
  activeSectionTitle?: string;
  /** Fallback name from profile/canonical when Personal Info section has corrupted content. */
  fallbackName?: string;
}

/** Join soft line-wraps into paragraphs; blank lines and structural rows stay separate. */
function normalizeDocBlocks(content: string, sectionTitle: string): string[] {
  const blocks: string[] = [];
  let paraBuf: string[] = [];

  const flushPara = () => {
    if (paraBuf.length) {
      blocks.push(paraBuf.join(' '));
      paraBuf = [];
    }
  };

  const isStructuralLine = (t: string) =>
    /^#{1,6}\s/.test(t) ||
    /^[-•●▪◦*]\s/.test(t) ||
    (/\|\s/.test(t) && t.split('|').length >= 2);

  for (const raw of content.split('\n')) {
    const trimmed = sanitizeSectionContentForCanvas(raw, sectionTitle).trim();
    if (!trimmed) {
      flushPara();
      blocks.push('');
      continue;
    }
    if (/^#{1,6}$/.test(trimmed)) continue;
    if (isStructuralLine(trimmed)) {
      flushPara();
      blocks.push(trimmed);
      continue;
    }
    paraBuf.push(trimmed);
  }
  flushPara();
  return blocks;
}

/** Parse plain content into formatted lines for the document view */
function DocContent({ content, sectionTitle, isFirstSection = false, isStreaming = false, fallbackName }: { content: string; sectionTitle: string; isFirstSection?: boolean; isStreaming?: boolean; fallbackName?: string }) {
  const { displayedText, isTyping } = useCanvasTypewriter(content, { isStreaming, speed: 12 });
  const textToRender = isStreaming && isTyping ? displayedText : content;
  const isContact = isFirstSection && isFirstSectionPersonalInfo(sectionTitle);
  if (isContact) {
    // Filter out work experience bullets that may have accidentally ended up in contact section
    // Handle various Unicode bullet characters: • ● ○ ◦ ▪ ▸ ► ‣ ⁃ ∙ ⋅ ◾ ◽
    const BULLET_CHARS = /^[\u2022\u2023\u2043\u204C\u204D\u2219\u25AA\u25AB\u25B8\u25BA\u25CF\u25CB\u25E6\u2605\u2606\u2713\u2714\u2717\u2718\u27A2\u2981\u29BE\u29BF•●○◦▪▸►‣⁃∙⋅◾◽\-*]\s*/;
    
    const isWorkBullet = (line: string) => {
      const t = line.trim();
      // Check for ANY bullet character (not just • - *)
      if (!BULLET_CHARS.test(t)) return false;
      // Must also contain action verbs typical of work experience
      return /\b(engineered|developed|led|managed|implemented|designed|built|created|delivered|achieved|drove|spearheaded|collaborated|established|improved|increased|reduced|launched|architected|integrated|optimized|automated|analyzed|executed|coordinated|directed|oversaw|mentored|defined|tracked)\b/i.test(t);
    };
    
    // Also check if line looks like work experience even without bullet
    const isWorkDescription = (line: string) => {
      const t = line.trim();
      // Long text with action verbs is likely work experience, not a name
      if (t.length > 50 && /\b(engineered|developed|implemented|designed|built|created|delivered|achieved|spearheaded|collaborated|architected|integrated)\b/i.test(t)) {
        return true;
      }
      return false;
    };
    
    const lines = textToRender
      .split('\n')
      .map((l) => sanitizeSectionContentForCanvas(l, sectionTitle))
      .map((l) => l.trim())
      .filter((l) => l && !/^#{1,6}$/.test(l) && !isWorkBullet(l) && !isWorkDescription(l));
    const JUNK_RE = /^(not specified|not provided|n\/a|na|none|null|undefined|unknown|to be updated|tbd|your |enter )/i;
    
    // CRITICAL: Additional safety check - a valid name must NOT:
    // - Start with any bullet character
    // - Contain action verbs typical of work experience
    // - Be longer than 60 characters (names are short)
    const isValidNameLine = (line: string): boolean => {
      const t = line.trim();
      if (!t || t.length > 60) return false;
      // Reject if starts with bullet
      if (BULLET_CHARS.test(t)) return false;
      // Reject if contains work experience action verbs
      if (/\b(engineered|developed|led|managed|implemented|designed|built|created|delivered|achieved|drove|spearheaded|collaborated|established|improved|increased|reduced|launched|architected|integrated|optimized|automated|analyzed|executed|coordinated|directed|oversaw|mentored|defined|tracked|responsible|streamlined|facilitated)\b/i.test(t)) return false;
      // Reject if looks like a sentence (has too many words)
      if (t.split(/\s+/).length > 8) return false;
      // Reject contact info
      if (/@/.test(t) || /linkedin|github|http/i.test(t) || /\+?\d{5,}/.test(t)) return false;
      return true;
    };
    
    // Find a proper name line, fall back to profile/canonical name if needed
    const nameLine = lines.find(l => isValidNameLine(l));
    const name = nameLine || fallbackName || '';
    const rest = lines.slice(1).filter((line, i, arr) => {
      const norm = line.toLowerCase().trim();
      if (!norm || JUNK_RE.test(norm)) return false;
      return arr.findIndex((l) => l.toLowerCase().trim() === norm) === i;
    });
    
    // Show loading placeholder if name is still loading
    const displayName = name.trim() || (
      <span className="inline-block animate-pulse bg-slate-200 dark:bg-slate-700 rounded h-8 w-48" />
    );
    
    return (
      <div style={{ marginBottom: '18px', paddingBottom: '12px', borderBottom: `1px solid ${DOC_BORDER}` }}>
        <div style={{
          fontFamily: DOC_FONT,
          fontSize: 'var(--ds-fs-page-title)',
          fontWeight: 700,
          color: DOC_TEXT_STRONG,
          lineHeight: 1.15,
          letterSpacing: '-0.01em',
          marginBottom: '6px',
          minHeight: '32px',
        }}>{displayName}</div>
        {rest.length > 0 && (
          <div style={{
            fontFamily: DOC_FONT,
            fontSize: 'var(--ds-fs-meta)',
            color: DOC_TEXT_MUTED,
            lineHeight: 1.5,
          }}>
            {rest.join('  ·  ')}
            {isTyping && (
              <span className="inline-block w-2 h-3.5 ml-1 bg-purple-500 animate-pulse align-middle rounded-[1px]" aria-hidden="true" />
            )}
          </div>
        )}
        {isTyping && rest.length === 0 && (
          <span className="inline-block w-2 h-3.5 ml-1 bg-purple-500 animate-pulse align-middle rounded-[1px]" aria-hidden="true" />
        )}
      </div>
    );
  }

  const blocks = normalizeDocBlocks(textToRender, sectionTitle);
  return (
    <div style={{ fontFamily: DOC_FONT, fontSize: DOC_FONT_SIZE, lineHeight: DOC_LINE_HEIGHT, color: DOC_TEXT }}>
      {blocks.map((line, i) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={i} style={{ height: '8px' }} />;

        const isLastBlock = i === blocks.length - 1;
        const showCursor = isTyping && isLastBlock;
        const cursor = showCursor ? (
          <span
            className="inline-block w-2 h-3.5 ml-1 bg-purple-500 animate-pulse align-middle rounded-[1px]"
            aria-hidden="true"
          />
        ) : null;

        const hashHeading = trimmed.match(/^#{1,6}\s+(.+)$/);
        if (hashHeading) {
          return (
            <div
              key={i}
              style={{ fontWeight: 700, fontSize: 'var(--ds-fs-meta)', marginTop: '8px', marginBottom: '2px' }}
            >
              {hashHeading[1]}
              {cursor}
            </div>
          );
        }

        // Bullet lines (* or •)
        if (/^[-•●▪◦*]\s/.test(trimmed)) {
          return (
            <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '4px' }}>
              <span style={{ flexShrink: 0, marginTop: '1px' }}>•</span>
              <span>{renderDocSegment(trimmed.replace(/^[-•●▪◦*]\s/, ''))}{cursor}</span>
            </div>
          );
        }

        // Role / company header lines (bold pattern: "Company | Role | Date" or "**Bold**")
        const hasBold = /\*\*[^*]+\*\*/.test(trimmed);
        if (hasBold || /\|\s/.test(trimmed)) {
          return (
            <div
              key={i}
              style={{ fontWeight: 600, marginTop: '7px', marginBottom: '3px' }}
            >
              {renderDocSegment(trimmed)}
              {cursor}
            </div>
          );
        }

        return (
          <div key={i} style={{ marginBottom: '6px' }}>
            {renderDocSegment(trimmed)}
            {cursor}
          </div>
        );
      })}
      {isTyping && blocks.length === 0 && (
        <span
          className="inline-block w-2 h-3.5 ml-1 bg-purple-500 animate-pulse align-middle rounded-[1px]"
          aria-hidden="true"
        />
      )}
    </div>
  );
}

function ResumePreview({
  sections,
  streamingSection,
  streamingContent,
  pendingSections,
  showDiff,
  originalSections,
  onSectionSave,
  isOptimizing = false,
  streamingAnchorRef,
  getSectionLabel,
  activeSectionTitle,
  fallbackName,
}: Omit<ResumePreviewProps, 'templateId' | 'matchScore' | 'originalMatchScore'>) {
  const [editingSection, setEditingSection] = React.useState<string | null>(null);
  const activeSectionRef = useRef<HTMLDivElement | null>(null);
  const { lingeringSection, dismiss, nudgeMessage, onSectionInteraction } = useCanvasIdleIntervention();

  useEffect(() => {
    if (activeSectionTitle && activeSectionRef.current) {
      activeSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [activeSectionTitle]);

  const isSectionPending = (title: string) => {
    if (!isOptimizing) return false;
    const st = title.toLowerCase();
    return pendingSections.some((ps) => {
      const lp = ps.toLowerCase();
      return st === lp || st.includes(lp) || lp.includes(st);
    });
  };

  const streamingIsNew = streamingSection
    ? !sections.find((s) => s.title.toLowerCase() === streamingSection.toLowerCase())
    : false;

  return (
    <div className="resume-paper-target mx-auto relative w-full max-w-full box-border">
      {/* Committed sections */}
      {sections.map((section, i) => {
        const isContact = i === 0 && isFirstSectionPersonalInfo(section.title);
        const label = getSectionLabel ? getSectionLabel(section.title) : section.title;
        const isCurrentlyStreaming =
          streamingSection != null &&
          section.title.toLowerCase() === streamingSection.toLowerCase();
        const displayContent = sanitizeSectionContentForCanvas(
          isCurrentlyStreaming ? streamingContent : section.content,
          section.title,
        );
        const origSection = originalSections?.find(
          (o) => o.title.toLowerCase() === section.title.toLowerCase()
        );
        const isEditing = editingSection === section.title;
        const contentHasPlaceholders = !isCurrentlyStreaming && hasPlaceholders(displayContent);

        if (showDiff && origSection) {
          return (
            <div
              key={section.title}
              className={`resume-section-container resume-section-block${isContact ? ' resume-section-container--contact' : ''}`}
            >
              <div className="ats-section-heading">{label}</div>
              <DiffSectionView title={label} original={origSection.content} optimized={displayContent} />
            </div>
          );
        }

        const isActive = !!(activeSectionTitle && section.title.toLowerCase() === activeSectionTitle.toLowerCase());

        return (
          <div
            key={section.title}
            ref={isActive ? activeSectionRef : undefined}
            className={[
              'resume-section-container transition-all duration-300',
              isContact ? 'resume-section-container--contact' : '',
              isCurrentlyStreaming ? 'is-streaming ring-2 ring-indigo-500 ring-offset-2 rounded-md' : '',
              isSectionPending(section.title) ? 'is-pending' : '',
              isActive ? 'ds-canvas-section--active' : '',
            ].filter(Boolean).join(' ')}
            style={{ position: 'relative' }}
            data-canvas-focus-card={isCurrentlyStreaming ? 'resume' : undefined}
            onMouseEnter={() => {
              if (!isOptimizing && !isCurrentlyStreaming) {
                onSectionInteraction(section.title);
              }
            }}
            onMouseLeave={() => dismiss()}
          >
            {isCurrentlyStreaming && <div ref={streamingAnchorRef} style={{ scrollMarginTop: 80 }} />}

            {isContact ? (
              /* Contact info header — compact edit button */
              !isCurrentlyStreaming && !isEditing && !isOptimizing && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 2 }}>
                  <button
                    type="button"
                    onClick={() => setEditingSection(section.title)}
                    className="section-edit-trigger"
                    data-no-export="true"
                    title="Edit contact information"
                    style={{ fontSize: '10px', padding: '1px 8px' }}
                  >
                    Edit
                  </button>
                </div>
              )
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div className="ats-section-heading">{label}</div>

                {!isCurrentlyStreaming && !isEditing && !isOptimizing && (
                  <button
                    type="button"
                    onClick={() => setEditingSection(section.title)}
                    className="section-edit-trigger"
                    data-no-export="true"
                    title="Edit this section"
                  >
                    Edit
                  </button>
                )}
                {contentHasPlaceholders && !isEditing && (
                  <span style={{
                    fontSize: '10px', color: '#92400e', background: '#fef3c7',
                    border: '1px solid #f59e0b', borderRadius: 3, padding: '1px 6px',
                    marginLeft: 6, flexShrink: 0,
                  }}>
                    Fill required
                  </span>
                )}
              </div>
            )}

            {isActive && isCurrentlyStreaming && (
              <div className="ds-section-overlay-skeleton">
                <div className="ds-section-skeleton__bar" style={{ width: '90%' }} />
                <div className="ds-section-skeleton__bar" style={{ width: '75%', animationDelay: '0.1s' }} />
                <div className="ds-section-skeleton__bar" style={{ width: '82%', animationDelay: '0.2s' }} />
              </div>
            )}

            <div className="resume-section-block">
              {isEditing ? (
                <InlineSectionEditor
                  content={displayContent || ''}
                  onSave={(newContent) => { onSectionSave?.(section.title, newContent); setEditingSection(null); }}
                  onCancel={() => setEditingSection(null)}
                />
              ) : contentHasPlaceholders ? (
                <PlaceholderContent
                  content={displayContent || ''}
                  onPlaceholderClick={() => setEditingSection(section.title)}
                />
              ) : (
                <DocContent content={displayContent || ''} sectionTitle={section.title} isFirstSection={i === 0} isStreaming={isCurrentlyStreaming} fallbackName={i === 0 ? fallbackName : undefined} />
              )}
            </div>
            {lingeringSection === section.title && (
              <div className="ds-canvas-nudge">
                <div className="ds-canvas-nudge__content">
                  <Sparkles className="h-3.5 w-3.5 shrink-0" style={{ color: 'var(--ds-brand)' }} />
                  <span>{nudgeMessage}</span>
                </div>
                <button type="button" onClick={dismiss} className="ds-canvas-nudge__close" aria-label="Dismiss">
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>
        );
      })}

      {/* Pending sections — just show the heading in muted text, no skeleton bars */}
      {pendingSections
        .filter((ps) => {
          const lp = ps.toLowerCase();
          if (streamingSection && lp === streamingSection.toLowerCase()) return false;
          return !sections.some((s) => {
            const st = s.title.toLowerCase();
            return st === lp || st.includes(lp) || lp.includes(st);
          });
        })
        .map((title) => (
          <div key={title} className="resume-section-container is-pending resume-section-block">
            <div className="ats-section-heading ats-section-heading--pending">{title}</div>
            <div className="ds-section-skeleton" aria-label={`Generating ${title}…`}>
              <div className="ds-section-skeleton__bar" style={{ width: '92%' }} />
              <div className="ds-section-skeleton__bar" style={{ width: '78%', animationDelay: '0.15s' }} />
              <div className="ds-section-skeleton__bar" style={{ width: '85%', animationDelay: '0.3s' }} />
              <div className="ds-section-skeleton__bar" style={{ width: '60%', animationDelay: '0.45s' }} />
            </div>
          </div>
        ))}

      {/* New section being streamed for the first time */}
      {streamingSection && streamingIsNew && (
        <div className="resume-section-container is-streaming ring-2 ring-indigo-500 ring-offset-2 rounded-md resume-section-block">
          <div ref={streamingAnchorRef} style={{ scrollMarginTop: 80 }} />
          <div className="ats-section-heading">{humanizeSectionName(streamingSection)}</div>
          <DocContent content={streamingContent} sectionTitle={streamingSection} isFirstSection={false} isStreaming={true} />
        </div>
      )}

      {/* Page footer */}
      <div
        style={{
          marginTop: '32px',
          borderTop: '1px solid rgba(0,0,0,0.08)',
          paddingTop: '8px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontFamily: DOC_FONT,
          fontSize: '8.5px',
          color: 'rgba(0,0,0,0.28)',
          letterSpacing: '0.04em',
          userSelect: 'none',
        }}
      >
        <span>GoGrabJob · AI-Optimized Resume</span>
        <span>A4 · 210 × 297 mm</span>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function StreamingCanvas({
  onApply,
  onDownload,
  onEmail,
  onBack,
  onSaveToProfile,
  onCoverLetter,
  onApplicationNote,
  onStartOptimization,
  onOpenConversation,
  onFindSimilarJobs,
  onInterviewPrep,
  onTrackApplications,
  jobAppliedInfo,
  className = '',
}: StreamingCanvasProps & {
  onCoverLetter?: () => void;
  onApplicationNote?: () => void;
}) {
  const { state, setTab, dispatch, setDocumentLocale } = useCanvas();
  const { t } = useTranslation();
  const { profile } = useUserProfile();
  const scrollRef = useRef<HTMLDivElement>(null);
  const streamingAnchorRef = useRef<HTMLDivElement>(null);
  const userScrolledUpRef = useRef(false);
  const atsRingGradientId = useId().replace(/:/g, '');
  const headerMatchScore = state.analysis?.matchScore ?? state.originalMatchScore ?? null;
  const [isExporting, setIsExporting] = useState<'pdf' | 'docx' | null>(null);
  const [showDiff, setShowDiff] = useState(false);
  const [showSkillGap, setShowSkillGap] = useState(false);

  useEffect(() => {
    const onOpenSkillGap = () => setShowSkillGap(true);
    window.addEventListener('ggj:agent-skill-gap', onOpenSkillGap);
    return () => window.removeEventListener('ggj:agent-skill-gap', onOpenSkillGap);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      userScrolledUpRef.current = distFromBottom > 120;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Auto-scroll so the user always sees what the AI is writing
  useEffect(() => {
    if (!state.streamingSection && state.phase !== 'optimization') return;
    
    window.dispatchEvent(new CustomEvent('ggj:canvas-focus'));

    const el = scrollRef.current;
    if (!el) return;

    const follow = () => {
      if (userScrolledUpRef.current && state.phase !== 'optimization') return;
      const focusCard = el.querySelector('[data-canvas-focus-card]') as HTMLElement | null;
      if (focusCard) {
        focusCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } else if (streamingAnchorRef.current) {
        streamingAnchorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } else {
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      }
    };

    requestAnimationFrame(follow);
  }, [state.streamingSection, state.streamingContent, state.phase]);

  // Scroll resume to top when optimization completes
  const prevPhaseRef = useRef(state.phase);
  useEffect(() => {
    const wasOptimizing = prevPhaseRef.current === 'optimization';
    prevPhaseRef.current = state.phase;
    if (wasOptimizing && state.phase !== 'optimization') {
      const el = scrollRef.current;
      if (el) {
        userScrolledUpRef.current = false;
        setTimeout(() => {
          el.scrollTo({ top: 0, behavior: 'smooth' });
        }, 300);
      }
    }
  }, [state.phase]);


  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      dispatch({ type: 'BACK_TO_LIST' });
    }
  };

  // ── Document Export (text-based PDF / DOCX — searchable, proper margins) ──
  const handleDownload = async (format: 'pdf' | 'docx') => {
    const sections = state.sections ?? [];
    if (sections.length === 0) return;
    if (isExporting) return;

    setIsExporting(format);
    try {
      // Map canvas templateId → docGenerator TemplateId via TEMPLATE_LIBRARY skin
      const skin = (TEMPLATE_LIBRARY.find((t) => t.id === state.templateId)?.skin ?? 'blue-classic') as TemplateId;
      const jobTitle = state.job?.title ?? '';
      const fileName = `Resume_${(state.job?.title ?? 'Optimized').replace(/[^a-zA-Z0-9]/g, '_')}`;
      const mappedSections = prepareSectionsForExport(sections).map((s) => ({
        title: s.title,
        content: s.content,
      }));

      // If a parent download handler is provided (AIAgentPanel), delegate entirely
      // to avoid a double-download (parent also generates and triggers the file).
      if (onDownload) {
        onDownload(format);
        return;
      }

      let blob: Blob;
      if (format === 'pdf') {
        blob = await generateResumePDF('', mappedSections, jobTitle, skin);
      } else {
        blob = await generateResumeDOCX('', mappedSections, jobTitle, skin);
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[StreamingCanvas] export failed:', err);
    } finally {
      setIsExporting(null);
    }
  };

  const handleCommitDraft = async () => {
    const draftSections = state.sections ?? [];
    if (!draftSections.length) return;
    dispatch({ type: 'SET_SAVING', isSaving: true });
    try {
      if (state.job?.id) {
        const resumeMd = draftSections.map((s) => `## ${s.title}\n${s.content}`).join('\n\n');
        await saveAgentArtifacts({
          jobId: String(state.job.id),
          jobTitle: state.job.title || 'Untitled role',
          companyName: state.job.company || 'Unknown company',
          jobUrl: state.job.url ?? undefined,
          resumeMarkdown: resumeMd,
        });
      }
      dispatch({ type: 'COMMIT_DRAFT' });
    } catch (err) {
      console.error('[StreamingCanvas] commit draft failed:', err);
    } finally {
      dispatch({ type: 'SET_SAVING', isSaving: false });
    }
  };

  const EMPTY_SECTIONS_REF = useRef<Array<{ title: string; content: string }>>([]);
  const sections = state.sections ?? EMPTY_SECTIONS_REF.current;
  const hasSections = sections.length > 0;
  const isContentStreaming = Boolean(state.streamingSection) || state.phase === 'optimization';
  
  // Fallback name from profile (available immediately) or canonical resume
  // This prevents showing corrupted work experience bullets as the header
  // IMPORTANT: Validate that displayName is not a work experience bullet (can happen if profile got corrupted)
  const isValidDisplayName = (name: string | undefined | null): boolean => {
    if (!name) return false;
    const t = name.trim();
    if (!t || t.length > 80) return false;
    // Reject if starts with bullet character
    if (/^[\u2022\u2023\u2043•●○◦▪▸►‣⁃∙⋅◾◽\-*]/.test(t)) return false;
    // Reject if contains work experience action verbs
    if (/\b(engineered|developed|led|managed|implemented|designed|built|created|delivered|achieved|drove|spearheaded|collaborated|established|improved|increased|reduced|launched|architected|integrated|optimized|automated|analyzed|executed|coordinated|directed|oversaw|mentored)\b/i.test(t)) return false;
    return true;
  };
  
  const profileName = isValidDisplayName(profile?.displayName) ? profile?.displayName : null;
  const canonicalName = isValidDisplayName(state.canonicalResume?.personalInfo?.fullName) ? state.canonicalResume?.personalInfo?.fullName : null;
  const resumeDataName = isValidDisplayName(state.originalResumeData?.name) ? state.originalResumeData?.name : null;
  
  const fallbackName = profileName || canonicalName || resumeDataName || '';
  const docLocale = normalizeDocumentLocale(state.documentLocale);

  return (
    <div
      className={`flex flex-col h-full ${className}`}
      data-agent-canvas-root="true"
      style={{ background: 'var(--ds-bg-page)' }}
    >
      {/* Header */}
      <div
        className="ds-canvas-header shrink-0 border-b"
        style={{ borderColor: 'var(--ds-border)', background: 'var(--ds-bg-card)' }}
      >
        <div className="ds-canvas-header__row ds-canvas-header__row--primary">
          <button
            type="button"
            onClick={handleBack}
            className="ds-canvas-header__back ds-btn ds-btn-ghost ds-btn-sm shrink-0 touch-manipulation"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span className="ds-canvas-header__back-label">{t('agent.canvas.backToJobs', 'Back to Jobs')}</span>
          </button>

          {state.job && (
            <div className="ds-canvas-header__job-meta min-w-0 flex-1">
              <span className="ds-canvas-header__job-title truncate">{state.job.title}</span>
              <span className="ds-canvas-header__job-company t-muted shrink-0">@ {state.job.company}</span>
              {jobAppliedInfo && (
                <JobWorkflowStatusBadge info={jobAppliedInfo} size="md" />
              )}
            </div>
          )}

          <div className="ds-canvas-doc-lang relative shrink-0">
            <label className="sr-only" htmlFor="canvas-doc-lang-select">
              {t('agent.canvas.documentLanguage.label', 'Document language')}
            </label>
            <select
              id="canvas-doc-lang-select"
              value={docLocale}
              onChange={(e) => setDocumentLocale(e.target.value as DocumentLocale, 'manual')}
              className="ds-canvas-doc-lang__select ds-btn ds-btn-ghost ds-btn-sm max-w-[148px] truncate text-[12px]"
              title={
                state.documentLocaleSource === 'auto'
                  ? t('agent.canvas.documentLanguage.autoHint', {
                      defaultValue: 'Auto-detected from job description — override if needed',
                      language: t(`agent.canvas.documentLanguage.options.${docLocale}`, docLocale),
                    })
                  : t('agent.canvas.documentLanguage.hint', 'Resume and letters will be generated in this language')
              }
            >
              {DOCUMENT_LOCALES.map((loc) => (
                <option key={loc} value={loc}>
                  {t(`agent.canvas.documentLanguage.options.${loc}`, loc.toUpperCase())}
                </option>
              ))}
            </select>
            {state.documentLocaleSource === 'auto' && (
              <span className="t-tag t-muted ml-1 hidden text-[10px] sm:inline">
                {t('agent.canvas.documentLanguage.auto', 'Auto')}
              </span>
            )}
          </div>

          {headerMatchScore != null && (
            <div
              className="ds-canvas-header-match ds-canvas-header-match--toolbar shrink-0"
              data-no-export="true"
              title={`ATS match score: ${Math.round(headerMatchScore)}%`}
            >
              <JobMatchRing score={headerMatchScore} gradientId={atsRingGradientId} compact />
            </div>
          )}
        </div>

        {(state.tab === 'resume' && hasSections && state.phase !== 'optimization') ||
        ((state.originalSections ?? null) && state.sections && state.sections.length > 0 && state.phase !== 'optimization') ? (
          <div className="ds-canvas-header__row ds-canvas-header__row--actions">
            <VersionSwitcher
              versions={state.versions}
              activeIndex={state.activeVersionIndex}
              onChange={(i) => dispatch({ type: 'SET_ACTIVE_VERSION', index: i })}
            />

            {state.tab === 'resume' && hasSections && state.phase !== 'optimization' && (
              <ActionMenu
                isExporting={isExporting}
                onDownload={handleDownload}
                onEmail={onEmail}
                onApply={onApply}
                onSkillGap={() => setShowSkillGap(true)}
                hasSkillGap={!!state.job}
                isApplied={!!state.isApplied}
                jobTitle={state.job?.title}
                inline
              />
            )}

            {(state.originalSections ?? null) && state.sections && state.sections.length > 0 && state.phase !== 'optimization' && (
              <button
                type="button"
                onClick={() => setShowDiff((v) => !v)}
                className={`ds-canvas-header__diff-btn ds-btn ds-btn-sm flex items-center gap-1.5 shrink-0 touch-manipulation ${showDiff ? 'ds-btn-primary' : 'ds-btn-secondary'}`}
                title="Toggle diff view to see what changed"
              >
                <GitCompare className="w-3.5 h-3.5" />
                <span className="ds-canvas-header__diff-label">{showDiff ? t('agent.canvas.resumeView', 'Resume View') : t('agent.canvas.showChanges', 'Show Changes')}</span>
              </button>
            )}
          </div>
        ) : null}
      </div>

      {/* Tab Bar */}
      <TabBar
        activeTab={state.tab}
        onTabChange={setTab}
        generatingTab={
          state.streamingSection === 'cover_letter'
            ? 'cover_letter'
            : state.streamingSection === 'application_note'
              ? 'application_note'
              : state.streamingSection
                ? 'resume'
                : null
        }
      />

      {/* Streaming spinner chip — floats top-right of content area while AI writes */}

      {/* Content Area — dark desk with floating A4 paper */}
      <div
        ref={scrollRef}
        className={`flex-1 relative ds-canvas-desk canvas-workspace-wrapper${state.streamingSection ? ' is-focused-generating' : ''}`}
      >

        {/* Streaming status — pinned under canvas header, not over the resume */}
        {state.streamingSection && (
          <div
            className="ds-canvas-streaming-chip"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              margin: '0 auto 16px',
              padding: '5px 12px 5px 9px',
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 500,
              pointerEvents: 'none',
              userSelect: 'none',
            }}
          >
            <span
              className="animate-spin"
              style={{
                width: 13, height: 13,
                borderRadius: '50%',
                border: '2px solid var(--ds-brand, #6366f1)',
                borderTopColor: 'transparent',
                flexShrink: 0,
                display: 'inline-block',
              }}
            />
            <span className="ds-canvas-streaming-chip__label">
              {humanizeSectionName(state.streamingSection)}
            </span>
            {state.streamingContent.trim().split(/\s+/).filter(Boolean).length > 0 && (
              <span className="ds-canvas-streaming-chip__meta">
                · {state.streamingContent.trim().split(/\s+/).filter(Boolean).length}w
              </span>
            )}
          </div>
        )}


        {state.tab === 'resume' && (hasSections || state.streamingSection || (state.pendingSections ?? []).length > 0 || state.canonicalResume || state.mode === 'CANVAS') && (
          <div className="ds-canvas-resume-stage">
            <div className="ds-canvas-resume-stage__paper">
              {state.canonicalResume && !showDiff && !state.streamingSection && state.phase !== 'optimization' && state.phase !== 'consent' ? (
                <div className="p-6 sm:p-8">
                  <ResumeCanvas data={state.canonicalResume} embedded />
                </div>
              ) : state.mode === 'CANVAS' && !showDiff && !state.streamingSection && !hasSections && state.phase !== 'optimization' ? (
                <div className="flex flex-col items-center justify-center gap-3 p-12 text-center min-h-[320px]">
                  <LoadingSpinner />
                  <p className="text-sm text-slate-600 dark:text-slate-400">Structuring your resume…</p>
                </div>
              ) : (
                <ResumePreview
                  sections={sections}
                  streamingSection={state.streamingSection}
                  streamingContent={state.streamingContent}
                  pendingSections={state.pendingSections ?? []}
                  showDiff={showDiff}
                  originalSections={state.originalSections ?? null}
                  isOptimizing={state.phase === 'optimization'}
                  streamingAnchorRef={streamingAnchorRef}
                  activeSectionTitle={state.activeSectionTitle}
                  onSectionSave={(title, newContent) => {
                    dispatch({ type: 'UPSERT_SECTION', title, content: newContent });
                  }}
                  getSectionLabel={(title) => title}
                  fallbackName={fallbackName}
                />
              )}
            </div>
            {headerMatchScore != null && (
              <div
                className="ds-canvas-resume-stage__match-ring ds-canvas-resume-stage__match-ring--beside-paper"
                data-no-export="true"
                title={`ATS match score: ${Math.round(headerMatchScore)}%`}
              >
                <JobMatchRing score={headerMatchScore} gradientId={atsRingGradientId} />
              </div>
            )}
          </div>
        )}

        {/* Save to Profile button — visible after optimization is complete */}
        {state.tab === 'resume' && hasSections && state.phase !== 'optimization' && onSaveToProfile && (
          <div className="flex justify-center py-4" data-no-export="true">
            <button
              type="button"
              onClick={onSaveToProfile}
              className="ds-btn ds-btn-sm flex items-center gap-1.5 touch-manipulation"
              style={{
                background: 'rgba(22, 101, 52, 0.15)',
                color: '#16a34a',
                border: '1px solid #16a34a',
              }}
            >
              <Save className="w-3.5 h-3.5" />
              {t('agent.canvas.saveToProfile', 'Save to Profile')}
            </button>
          </div>
        )}

        {state.tab === 'resume' && !hasSections && !state.canonicalResume && !state.streamingSection && (state.pendingSections ?? []).length === 0 && (
          <div className="ds-canvas-empty">
            <div className="ds-canvas-empty__orb">
              <Sparkles style={{ width: 36, height: 36, color: '#fff' }} />
            </div>
            <div className="ds-canvas-empty__copy">
              <div className="ds-canvas-empty__title">{t('agent.canvas.empty.title', 'Resume Canvas')}</div>
              {state.job ? (
                <p className="ds-canvas-empty__desc">
                  {t('agent.canvas.empty.readyDesc', {
                    defaultValue: 'Ready to optimize for {{title}} at {{company}}. Start AI optimization below, or open the agent to discuss changes.',
                    title: state.job.title,
                    company: state.job.company,
                  })}
                </p>
              ) : (
                <p className="ds-canvas-empty__desc">
                  {t('agent.canvas.empty.noJobDesc', 'Your AI-optimized resume will appear here. Tell the agent which job to optimize for, or pick a job from the list.')}
                </p>
              )}
            </div>
            <div className="ds-canvas-empty__actions">
              {state.job && onStartOptimization && (
                <button
                  type="button"
                  onClick={onStartOptimization}
                  className="ds-btn ds-btn-primary ds-canvas-empty__cta touch-manipulation"
                >
                  <Sparkles className="h-4 w-4 shrink-0" aria-hidden />
                  {t('agent.canvas.empty.startOptimization', 'Start AI optimization')}
                </button>
              )}
              {onOpenConversation && (
                <button
                  type="button"
                  onClick={onOpenConversation}
                  className="ds-btn ds-btn-secondary ds-canvas-empty__cta touch-manipulation"
                >
                  {t('agent.canvas.empty.openConversation', 'Open conversation')}
                </button>
              )}
              {state.job && onCoverLetter && (
                <button
                  type="button"
                  onClick={onCoverLetter}
                  className="ds-btn ds-btn-ghost ds-canvas-empty__chip touch-manipulation"
                >
                  {t('agent.canvas.empty.writeCoverLetter', 'Write cover letter')}
                </button>
              )}
              {state.job && (
                <button
                  type="button"
                  onClick={() => setShowSkillGap(true)}
                  className="ds-btn ds-btn-ghost ds-canvas-empty__chip touch-manipulation"
                >
                  {t('agent.canvas.empty.analyzeSkillGaps', 'Analyze skill gaps')}
                </button>
              )}
            </div>
          </div>
        )}

        {state.tab === 'cover_letter' && (
          <CoverLetterView
            content={state.coverLetter}
            streamingContent={state.streamingContent}
            isStreaming={state.streamingSection === 'cover_letter'}
            candidateName={state.originalResumeData?.personalInfo?.fullName}
            candidateEmail={state.originalResumeData?.personalInfo?.email}
            candidatePhone={state.originalResumeData?.personalInfo?.phone}
            jobTitle={state.job?.title}
            company={state.job?.company}
            onGenerate={onCoverLetter}
          />
        )}

        {state.tab === 'application_note' && (
          <ApplicationNoteView
            content={state.applicationNote}
            streamingContent={state.streamingContent}
            isStreaming={state.streamingSection === 'application_note'}
            candidateName={state.originalResumeData?.personalInfo?.fullName}
            jobTitle={state.job?.title}
            company={state.job?.company}
            onGenerate={onApplicationNote}
          />
        )}

      </div>

      {/* ── Skill Gap Modal — full-viewport backdrop, dialog centered on page ── */}
      {showSkillGap && state.job && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setShowSkillGap(false); }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9000,
            background: 'rgba(0,0,0,0.45)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'max(24px, env(safe-area-inset-top)) max(24px, env(safe-area-inset-right)) max(24px, env(safe-area-inset-bottom)) max(24px, env(safe-area-inset-left))',
          }}
        >
          {/* Dialog card */}
          <div
            className="ds-skill-gap-modal"
            style={{
              width: 'min(560px, calc(100vw - 48px))',
              maxHeight: 'calc(100vh - 80px)',
              background: 'var(--ds-bg-card)',
              borderRadius: 18,
              border: '1px solid var(--ds-border)',
              boxShadow: '0 8px 16px rgba(0,0,0,0.08), 0 32px 80px rgba(0,0,0,0.22)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Modal title bar */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 20px 14px 22px',
              borderBottom: '1px solid var(--ds-border, rgba(0,0,0,0.07))',
              background: 'var(--ds-bg-tinted, #fafafa)',
              flexShrink: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: 'rgba(99,102,241,0.10)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <BarChart2 style={{ width: 16, height: 16, color: 'var(--ds-brand,#6366f1)' }} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ds-text-strong)' }}>Skill Gap Analysis</div>
                  <div style={{ fontSize: 11, color: 'var(--ds-text-muted)', marginTop: 1 }}>
                    {state.job.title}{state.job.company ? ` · ${state.job.company}` : ''}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowSkillGap(false)}
                title="Close"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 30, height: 30, borderRadius: 8,
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--ds-text-muted)',
                  transition: 'background 0.12s',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--ds-bg-hover)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}
              >
                <X style={{ width: 15, height: 15 }} />
              </button>
            </div>

            {/* Scrollable body */}
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {!state.analysis ? (
                <div style={{ padding: '32px 24px', textAlign: 'center', color: 'var(--ds-text-muted)', fontSize: 13 }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>📊</div>
                  <div style={{ fontWeight: 600, color: 'var(--ds-text-strong)', marginBottom: 6 }}>ATS analysis not yet run</div>
                  <div>Ask the agent to <strong>"optimize my resume for this role"</strong> to unlock the full skill gap breakdown.</div>
                </div>
              ) : (
              <SkillGapPanel
                analysis={state.analysis}
                originalMatchScore={state.originalMatchScore}
                jobTitle={state.job.title}
                company={state.job.company}
              />
              )}
            </div>
          </div>
        </div>
      )}

      {(state.isDraftDirty || !state.committedSections?.length) && hasSections && state.phase !== 'optimization' && (
        <div className="ds-canvas-draft-bar ds-canvas-draft-bar--sticky">
          <span className="ds-canvas-draft-bar__label">
            {t('agent.canvas.draft.unsaved', 'Unsaved changes — review on canvas, then save')}
          </span>
          <div className="ds-canvas-draft-bar__actions">
            <button
              type="button"
              className="ds-btn ds-btn-ghost ds-btn-sm"
              onClick={() => dispatch({ type: 'DISCARD_DRAFT' })}
              disabled={!state.committedSections?.length || state.isSaving}
            >
              {t('agent.canvas.draft.discard', 'Discard')}
            </button>
            <button
              type="button"
              className="ds-btn ds-btn-primary ds-btn-sm"
              onClick={() => void handleCommitDraft()}
              disabled={state.isSaving}
            >
              {state.isSaving
                ? t('agent.canvas.draft.saving', 'Saving…')
                : t('agent.canvas.draft.save', 'Save resume')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default StreamingCanvas;
